import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { renderModal } from 'utils/modal_util';
import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { formatDateModified } from 'utils/format_date';
import slug from 'utils/slug';

import wallPreviewModule from 'domains/wall_previews';

import Modal from 'components/shared/Modal';
import Alert from 'components/shared/Alert';
import NavigationTile from 'components/shared/NavigationTile';

import type { WallPreview } from 'domains/wall_previews';
import type { NavigationTileMenuItem } from 'components/shared/NavigationTile/NavigationTile';

type ManageWallPreviewsModalProps = {
  onClose: () => void;
};

function ManageWallPreviewsModal({ onClose }: ManageWallPreviewsModalProps) {
  const dispatch = useAppDispatch();
  const wallPreviews = useAppSelector(wallPreviewModule.selectors.selectAll);

  const [pendingWallPreview, setPendingWallPreview] = useState<WallPreview | null>(null);

  function handleSelectWallPreview(wp: WallPreview) {
    window.open(wp.url, '_blank');
  }

  function handleDeleteWallPreview() {
    if (!pendingWallPreview) return;

    dispatch(wallPreviewModule.actions.destroy.pending(pendingWallPreview.id));
    setPendingWallPreview(null);
  }

  const pendingWallPreviewMenuId = useMemo(() => {
    if (!pendingWallPreview) return '';

    return `${slug(pendingWallPreview.name)}-${pendingWallPreview.id}-navigation-tile`;
  }, [pendingWallPreview]);

  const previewTiles = useMemo(
    () =>
      wallPreviews.map((wp) => {
        const menuItems: NavigationTileMenuItem[] = [
          {
            label: (
              <span>
                Delete <span className="screen-reader-text">{wp.name}</span>
              </span>
            ),
            icon: <FontAwesomeIcon icon="trash-alt" />,
            click: () => {
              setPendingWallPreview(wp);
            },
          },
        ];

        return (
          <li key={wp.id}>
            <NavigationTile
              image={wp.url}
              details={`Saved: ${formatDateModified(wp.created_at)}`}
              item={wp}
              menuItems={menuItems}
              name={wp.name}
              clickHandler={() => handleSelectWallPreview(wp)}
            />
          </li>
        );
      }),
    [wallPreviews]
  );

  return (
    <Modal
      title="Saved Wall Previews"
      labelledBy="saved-wall-previews"
      lastFocusedElementId="content-menu-trigger-saved-wall-previews"
      onClose={onClose}
    >
      {!!pendingWallPreview &&
        renderModal(
          <Alert
            onClose={() => setPendingWallPreview(null)}
            labelledBy="confirm-delete-preview-dialog"
            lastFocusedElementId={pendingWallPreviewMenuId}
            title="Confirm Delete"
            message="Are you sure you want to delete this saved wall preview?"
            onCancel={() => setPendingWallPreview(null)}
            onContinue={() => handleDeleteWallPreview()}
          />
        )}
      <ul className="modal-list-items">{previewTiles}</ul>
    </Modal>
  );
}

export default ManageWallPreviewsModal;
